import { useMemo } from 'react';
import type { CalendarEvent } from '../../hooks/useEvents';
import {
  TutorCardWrapper as Card,
  TutorInfo as CardInfo,
  TutorName as Title,
  CornerTagContainer,
  CornerTag,
  NameAndPriceWrapper,
  TagWrap,
  Tag,
} from '../../styles/search/search.style';
import { styled } from '../../styles/stitches.config';
import { useUser } from '../../contextManager/user.context';
import { CalendarBadge } from './CalendarBadge';
import { formatInTimeZone } from 'date-fns-tz';
import { formatRangeInTz } from '../../utils/timezones';
import { getFirstName } from '../../utils/name';

const Row = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const Subtle = styled('div', {
  color: '$gray10',
  fontSize: '$sm',
});

const When = styled('div', {
  fontWeight: 600,
  color: '$gray12',
  fontSize: '$md',
});

function fmtRange(startISO: string, endISO: string, tz: string) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  const date = formatInTimeZone(s, tz, 'EEE d MMM');
  const range = formatRangeInTz(s, e, tz);
  return { date, range };
}



function getCounterpartyName(ev: CalendarEvent, currentUserId?: string) {
  if (!currentUserId) return getFirstName(ev.parentName || ev.tutorName);
  if (ev.tutorId === currentUserId) return getFirstName(ev.parentName);
  if (ev.parentId === currentUserId) return getFirstName(ev.tutorName);
  return getFirstName(ev.parentName || ev.tutorName);
}


type Props = { event: CalendarEvent; onOpen?: (ev: CalendarEvent) => void };


/** Default function export (hoisted) avoids TDZ in circular graphs */
export default function LessonCard({ event, onOpen}:  Props ) {
  const { userDetails } = useUser();
  const myTz = userDetails?.timezone || 'Europe/London';
  const { date, range } = fmtRange(event.startAt, event.endAt, myTz);

  const typeTag =
    event.eventType === 'lesson' ? (
      <CornerTag color="blue">Lesson</CornerTag>
    ) : (
      <CornerTag color="yellow">Intro Call</CornerTag>
    );

  const who = useMemo(
    () => getCounterpartyName(event, userDetails?.id),
    [event, userDetails?.id]
  );

  const baseTitle = event.title || (event.eventType === 'lesson' ? 'Lesson' : 'Intro Call');
  const fullTitle = who ? `${baseTitle} with ${who}` : baseTitle;
  const baseSeriesTitle = event.seriesTitle;
  const fullSeriesTitle = who ? `${baseSeriesTitle} with ${who}` : baseTitle;
  return (
    <Card
      css={{ cursor: onOpen ? 'pointer' : 'default' }}
      onClick={() => onOpen?.(event)}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => ((e.key === 'Enter' || e.key === ' ') && onOpen(event)) : undefined}
    >
      <CornerTagContainer>{typeTag}</CornerTagContainer>
      <CalendarBadge date={event.startAt} />

      <CardInfo>
        <NameAndPriceWrapper>
          <Title>{fullTitle}</Title>
          <div />
        </NameAndPriceWrapper>

        <Row>
          <When>{date}</When>
          <Subtle>{range}</Subtle>
        </Row>

        {(event.seriesTitle || event.seriesId) && (
          <TagWrap css={{ marginTop: '0.75rem' }}>
            {event.seriesTitle && <Tag isMatch={true}>{fullSeriesTitle}</Tag>}
          </TagWrap>
        )}
      </CardInfo>
    </Card>
  );
}
